import './App.css';
import { generateCSV, downloadFile } from './utils/export';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge-2";
import { Heart, Activity, Wind, Trash2, Download } from 'lucide-react';
import { Button } from "@/components/ui/button";

/**
 * HistoryPanel Component
 * Displays past measurement history with visual cards
 */
const HistoryPanel = ({ measurements, onClear }) => {
  const handleExport = () => {
    if (measurements.length === 0) return;
    const csv = generateCSV(measurements);
    downloadFile(csv, `vital_signs_history_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  /**
   * Format timestamp to readable date/time
   */
  const formatTimestamp = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (measurements.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground bg-muted/10 rounded-xl border border-dashed">
        <Activity className="h-12 w-12 opacity-20 mb-4" />
        <p className="font-medium">No measurements yet</p>
        <p className="text-sm">Complete a scan to see your history here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-muted-foreground">
            {measurements.length} Records
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} className="gap-2">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          <Button variant="ghost" size="sm" onClick={onClear} className="text-destructive hover:bg-destructive/10">
            <Trash2 className="h-4 w-4" /> Clear
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {measurements.map((measurement, index) => (
          <Card key={index} className="overflow-hidden border-border/50 hover:border-primary/50 transition-colors group">
            <div className="flex flex-col sm:grid sm:grid-cols-5 h-full">

              {/* Left: Image / Date */}
              <div className="sm:col-span-2 bg-muted/30 relative border-b sm:border-b-0 sm:border-r h-48 sm:h-auto">
                {measurement.image ? (
                  <img src={measurement.image} alt="Scan" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-muted">
                    <Activity className="h-8 w-8 text-muted-foreground/20" />
                  </div>
                )}
                <div className="absolute top-2 left-2">
                  <Badge variant="secondary" className="text-[10px] backdrop-blur-md bg-background/80 shadow-sm border-0">
                    {formatTimestamp(measurement.timestamp)}
                  </Badge>
                </div>
              </div>

              {/* Right: Stats */}
              <div className="sm:col-span-3 p-4 flex flex-col justify-center gap-4">
                {/* Heart Rate */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Heart className="h-4 w-4 text-red-500" />
                    <span className="text-xs font-medium uppercase tracking-wider">Heart Rate</span>
                  </div>
                  <span className="text-xl font-bold">{Math.round(measurement.heartRate) || '--'} <span className="text-xs font-normal text-muted-foreground">bpm</span></span>
                </div>

                {/* HRV */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Activity className="h-4 w-4 text-violet-500" />
                    <span className="text-xs font-medium uppercase tracking-wider">HRV</span>
                  </div>
                  <span className="text-xl font-bold text-muted-foreground">{Math.round(measurement.hrvRmssd) || '--'} <span className="text-xs font-normal text-muted-foreground">ms</span></span>
                </div>

                {/* Breathing */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Wind className="h-4 w-4 text-sky-500" />
                    <span className="text-xs font-medium uppercase tracking-wider">Breathing</span>
                  </div>
                  <span className="text-xl font-bold text-muted-foreground">{Math.round(measurement.breathingRate) || '--'} <span className="text-xs font-normal text-muted-foreground">rpm</span></span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
export default HistoryPanel;
